import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

/**
 * Every timestamp in the admin UI is shown in Korea time.
 *
 * It used to be Beijing (UTC+8) here while a few screens rendered Seoul (UTC+9) and others
 * fell back to whatever timezone the operator's own machine was in, so the same instant
 * appeared three different ways. An operator hit this on TM20260729A0004: the order list
 * showed a modified time of 10:12 and the order log showed 11:12:39 for the very same
 * status change, and there was no way to tell they were the same event.
 *
 * Korea time because that is what the money runs on — the banks, BillGate and settlement
 * all keep their books in KST, so a payment near midnight otherwise lands on a different
 * calendar day in the admin than on the bank statement. Warehouse staff in China read one
 * hour ahead of their wall clock; the columns say KST so it is not a guess.
 */
export const ADMIN_TIMEZONE = 'Asia/Seoul';

/**
 * Convert a UTC timestamp to Korea time and format it.
 * @param utcTime - UTC time string or Date object
 * @param format - Format string (default: 'YYYY-MM-DD HH:mm:ss')
 * @returns Formatted KST string
 */
export const formatUTCToLocal = (
  utcTime: string | Date | null | undefined,
  format: string = 'YYYY-MM-DD HH:mm:ss'
): string => {
  if (!utcTime) return '';

  try {
    return dayjs.utc(utcTime).tz(ADMIN_TIMEZONE).format(format);
  } catch (error) {
    // console.error('[TimeFormat] Error formatting time:', error);
    return '';
  }
};

/**
 * @deprecated Same as `formatUTCToLocal` now that the whole admin is on KST. Kept so the
 * screens that were explicitly switched to Seoul keep working; prefer `formatUTCToLocal`.
 */
export const formatUTCToSeoul = (
  utcTime: string | Date | null | undefined,
  format: string = 'YYYY-MM-DD HH:mm:ss'
): string => {
  if (!utcTime) return '';
  try {
    return dayjs.utc(utcTime).tz('Asia/Seoul').format(format);
  } catch (error) {
    return '';
  }
};

/**
 * Convert a UTC timestamp to the KST calendar date only.
 * @param utcTime - UTC time string or Date object
 * @returns Formatted KST date string (YYYY-MM-DD)
 */
export const formatUTCToLocalDate = (
  utcTime: string | Date | null | undefined
): string => {
  return formatUTCToLocal(utcTime, 'YYYY-MM-DD');
};

/**
 * Convert a UTC timestamp to a KST datetime.
 * @param utcTime - UTC time string or Date object
 * @returns Formatted KST datetime string (YYYY-MM-DD HH:mm)
 */
export const formatUTCToLocalDateTime = (
  utcTime: string | Date | null | undefined
): string => {
  return formatUTCToLocal(utcTime, 'YYYY-MM-DD HH:mm');
};

/**
 * Convert UTC time to dayjs object in Beijing timezone
 * @param utcTime - UTC time string or Date object
 * @returns dayjs object in Korea timezone
 */
export const utcToLocalDayjs = (
  utcTime: string | Date | null | undefined
): dayjs.Dayjs | null => {
  if (!utcTime) return null;

  try {
    return dayjs.utc(utcTime).tz(ADMIN_TIMEZONE);
  } catch (error) {
    // console.error('[TimeFormat] Error converting to dayjs:', error);
    return null;
  }
};

/**
 * Convert a KST admin input (e.g. a date filter) to UTC for the backend.
 * @param localTime - KST time string or Date object or dayjs object
 * @returns UTC time string in ISO format
 */
export const localToUTC = (
  localTime: string | Date | dayjs.Dayjs | null | undefined
): string | null => {
  if (!localTime) return null;

  try {
    return dayjs.tz(localTime, ADMIN_TIMEZONE).utc().toISOString();
  } catch (error) {
    // console.error('[TimeFormat] Error converting to UTC:', error);
    return null;
  }
};
